import { cn, isMac as detectedIsMac } from '@heidi/utils';
import {
  ArrowBigUpDash,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Command,
  Option,
} from 'lucide-react';
import * as React from 'react';
import { defineMessages, type IntlShape } from 'react-intl';
import { Key as KeyEnum } from 'ts-key-enum';
import { useDesignSystemIntl } from '../hooks/use-design-system-intl/use-design-system-intl';
import { Tooltip, TooltipArrow, TooltipContent, TooltipTrigger } from '../tooltip';
import { TypographyCaption, typographyVariant } from '../typography';
import { kbdVariants } from './kbd.styles';
import type { KbdProps } from './kbd.types';

const kbdTooltipMessages = defineMessages({
  command: {
    defaultMessage: 'Command',
    id: 'pq5a61SXfn',
    description: 'Tooltip label for the macOS Command modifier key',
  },
  shift: {
    defaultMessage: 'Shift',
    id: '1GBtwqO9Sv',
    description: 'Tooltip label for the Shift modifier key',
  },
  up: {
    defaultMessage: 'Up',
    id: 'RJBWZ9ct9K',
    description: 'Tooltip label for the Up arrow key',
  },
  down: {
    defaultMessage: 'Down',
    id: 'Sl+2VvBspI',
    description: 'Tooltip label for the Down arrow key',
  },
  left: {
    defaultMessage: 'Left',
    id: 'POjsYG2qHl',
    description: 'Tooltip label for the Left arrow key',
  },
  right: {
    defaultMessage: 'Right',
    id: 'iY8NpxweiX',
    description: 'Tooltip label for the Right arrow key',
  },
  option: {
    defaultMessage: 'Option',
    id: 'oLYq5/G7uY',
    description: 'Tooltip label for the macOS Option modifier key',
  },
  capsLock: {
    defaultMessage: 'Caps Lock',
    id: 'MVp3WDAIDu',
    description: 'Tooltip label for the Caps Lock key',
  },
});

function renderModifierIcon(
  key: KeyEnum,
  isMac: boolean,
  compact: boolean,
  contentClassName?: string
): React.ReactNode {
  const iconClass = cn(compact ? 'size-2.5' : 'size-3', contentClassName);
  const textVariant = compact ? 'footnote' : 'caption';
  const textClass = cn(
    typographyVariant({ variant: textVariant }),
    'leading-none text-text-secondary',
    contentClassName
  );

  switch (key) {
    case KeyEnum.Meta:
      return isMac ? (
        <Command className={iconClass} />
      ) : (
        <span className={textClass}>Ctrl</span>
      );
    case KeyEnum.Shift:
      return <ArrowBigUpDash className={iconClass} />;
    case KeyEnum.ArrowUp:
      return <ChevronUp className={iconClass} />;
    case KeyEnum.ArrowDown:
      return <ChevronDown className={iconClass} />;
    case KeyEnum.ArrowLeft:
      return <ChevronLeft className={iconClass} />;
    case KeyEnum.ArrowRight:
      return <ChevronRight className={iconClass} />;
    case KeyEnum.Alt:
      return isMac ? (
        <Option className={iconClass} />
      ) : (
        <span className={textClass}>Alt</span>
      );
    case KeyEnum.Control:
      return <span className={textClass}>Ctrl</span>;
    case KeyEnum.CapsLock:
      return <span className={textClass}>⇪</span>;
    case KeyEnum.Fn:
      return <span className={textClass}>fn</span>;
    default:
      return key;
  }
}

function getModifierTooltipLabel(
  key: KeyEnum,
  isMac: boolean,
  formatMessage: IntlShape['formatMessage']
): string {
  switch (key) {
    case KeyEnum.Meta:
      return isMac ? formatMessage(kbdTooltipMessages.command) : 'Ctrl';
    case KeyEnum.Shift:
      return formatMessage(kbdTooltipMessages.shift);
    case KeyEnum.ArrowUp:
      return formatMessage(kbdTooltipMessages.up);
    case KeyEnum.ArrowDown:
      return formatMessage(kbdTooltipMessages.down);
    case KeyEnum.ArrowLeft:
      return formatMessage(kbdTooltipMessages.left);
    case KeyEnum.ArrowRight:
      return formatMessage(kbdTooltipMessages.right);
    case KeyEnum.Alt:
      return isMac ? formatMessage(kbdTooltipMessages.option) : 'Alt';
    case KeyEnum.Control:
      return 'Ctrl';
    case KeyEnum.CapsLock:
      return formatMessage(kbdTooltipMessages.capsLock);
    case KeyEnum.Fn:
      return 'fn';
    default:
      return String(key);
  }
}

const Kbd = React.forwardRef<HTMLElement, KbdProps>((allProps, ref) => {
  const {
    'data-testid': testId,
    size = 'default',
    isMac: isMacProp,
    tooltip = false,
    inline: inlineProp = false,
    contentClassName,
    className,
    children,
    ...restProps
  } = allProps;

  const isMac = isMacProp ?? detectedIsMac;
  const intl = useDesignSystemIntl();

  const compact = size === 'compact';
  const content =
    allProps.keyType === 'modifier' ? (
      renderModifierIcon(allProps.children, isMac, compact, contentClassName)
    ) : (
      <span
        className={cn(
          typographyVariant({ variant: compact ? 'footnote' : 'caption' }),
          'leading-none text-text-secondary',
          contentClassName
        )}
      >
        {children}
      </span>
    );

  const kbdElement = (
    <kbd
      ref={ref}
      data-testid={testId}
      className={cn(kbdVariants({ size, inline: inlineProp }), className)}
      {...restProps}
    >
      {content}
    </kbd>
  );

  const tooltipLabel =
    typeof tooltip === 'string'
      ? tooltip
      : tooltip && allProps.keyType === 'modifier'
        ? getModifierTooltipLabel(allProps.children, isMac, intl.formatMessage)
        : null;

  if (!tooltipLabel) {
    return kbdElement;
  }

  return (
    <Tooltip delayDuration={100}>
      <TooltipTrigger asChild>{kbdElement}</TooltipTrigger>
      <TooltipContent side="top">
        <TypographyCaption>{tooltipLabel}</TypographyCaption>
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
});
Kbd.displayName = 'Kbd';

export { Kbd };
